import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { SIDEBAR_GRAPH_WIDTH } from "../../../constants/constants";

type TrendCurvesProps = {
  lineGraphData: {
    labels: string[];
    datasets: { data: number[]; borderColor: string }[];
  };
  width?: number;
  trendsCurveExpandedView: boolean;
};

const TrendCurvesGraph: React.FC<TrendCurvesProps> = ({
  lineGraphData,
  width = SIDEBAR_GRAPH_WIDTH,
  trendsCurveExpandedView,
}) => {
  const chartRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!chartRef.current || !tooltipRef.current) return;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const tooltip = d3.select(tooltipRef.current);

    const margin = { top: 20, right: 30, bottom: 50, left: 40 };
    const height = 250 - margin.top - margin.bottom;
    const adjustedWidth = width - margin.left - margin.right;

    const x = d3
      .scalePoint()
      .domain(lineGraphData.labels)
      .range([0, adjustedWidth])
      .padding(0.5);

    const y = d3.scaleLinear().domain([0, 100]).range([height, 0]);

    const line = d3
      .line<number>()
      .x((_, i) => x(lineGraphData.labels[i]) ?? 0)
      .y(d => y(d))
      .curve(d3.curveMonotoneX);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X-axis
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .selectAll("text")
      .style("font-size", `${trendsCurveExpandedView ? "14px" : "10px"}`)
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    // Y-axis with dynamic font size
    g.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("font-size", trendsCurveExpandedView ? "14px" : "10px");

    // Group for all lines
    const linesGroup = g.append("g");

    lineGraphData.datasets.forEach((dataset, datasetIndex) => {
      // Append line
      const path = linesGroup
        .append("path")
        .datum(dataset.data)
        .attr("fill", "none")
        .attr("stroke", dataset.borderColor)
        .attr("stroke-width", 1.5)
        .attr("d", line)
        .attr("class", `line-${datasetIndex}`)
        .style("opacity", 0.4);

      // Append circles at each data point
      const circles = g
        .selectAll(`.point-${datasetIndex}`)
        .data(dataset.data)
        .enter()
        .append("circle")
        .attr("cx", (_, i) => x(lineGraphData.labels[i]) ?? 0)
        .attr("cy", d => y(d))
        .attr("r", 4)
        .attr("fill", dataset.borderColor)
        .attr("stroke", "white")
        .attr("stroke-width", 1.5)
        .attr("class", `circle-${datasetIndex}`)
        .style("opacity", 0.4) // Default opacity
        .style("cursor", "pointer")
        .on("mouseover", (event, d) => {
          const [mouseX, mouseY] = d3.pointer(event);
          const index = dataset.data.indexOf(d);
          const label = lineGraphData.labels[index];

          tooltip
            .style("visibility", "visible")
            .style("left", `${mouseX + margin.left + 20}px`)
            .style("top", `${mouseY + margin.top - 10}px`)
            .html(
              `<div style="padding:5px; background:white; border:1px solid #ccc; border-radius:4px;">
                <span style="color:${dataset.borderColor}; font-weight:bold;">●</span>
                <strong>${label}</strong><br/>
                Value: <strong>${d}</strong>
              </div>`
            );
        })
        .on("mousemove", event => {
          const [mouseX, mouseY] = d3.pointer(event);
          tooltip
            .style("left", `${mouseX + margin.left + 20}px`)
            .style("top", `${mouseY + margin.top - 10}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
        });

      // Add hover effect on the entire line
      path
        .on("mouseover", () => {
          path.style("opacity", 1).style("cursor", "pointer"); // Set cursor pointer
          linesGroup
            .selectAll("path")
            .filter(d => d !== dataset.data)
            .style("opacity", 0.2);
          g.selectAll("circle").style("opacity", 0.2);
          circles.style("opacity", 1).style("cursor", "pointer"); // Set cursor pointer for circles
        })
        .on("mouseout", () => {
          linesGroup.selectAll("path").style("opacity", 0.4);
          g.selectAll("circle").style("opacity", 0.4);
        });
    });
  }, [lineGraphData, width, trendsCurveExpandedView]);

  return (
    <div style={{ position: "relative" }}>
      <svg ref={chartRef} width={width} height={250}></svg>
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          fontSize: "12px",
        }}
      ></div>
    </div>
  );
};

export default TrendCurvesGraph;
